import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { TimesheetsEmployeeIndexTable } from '@app/modules/employees/services';
import { Action } from '@app/modules/table/interfaces/action.interface';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { ReportDownloadService, TimesheetEntryService } from '@app/modules/time-tracking/services';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { PayrollIncomeType } from '@time-tracking/interfaces/settings.interface';
import { PayrollValidationHttpErrorCodes } from '@time-tracking/meta/time-tracking.meta';
import FileSaver from 'file-saver';

@Component({
    templateUrl: './time-tracking-show.template.html',
})
export class TimeTrackingShow {
    @ViewChild('confirmDeleteModal') confirmDeleteModal!: ConfirmationDialogComponent;

    employee = new Employee();

    isLoading = false;

    table: TimesheetsEmployeeIndexTable | undefined;

    timeTrackingAdmin = this.auth.isAssignedTimeTrackingAdmin();
    hasTimeClock = false;

    constructor(
        public auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notifyService: NotifyService,
        private timesheetEntryService: TimesheetEntryService,
        private reportDownloadService: ReportDownloadService,
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private featureService: FeatureService
    ) {
        this.employee = this.route.snapshot.data.models.get('employee');
    }

    async ngOnInit(): Promise<void> {
        let hasPayrollIntegration = false;
        let payrollIncomeTypes: PayrollIncomeType[] = [];
        const settings = await this.timeTrackingSettingsService.get();
        hasPayrollIntegration = settings.payrollIntegration ?? false;
        payrollIncomeTypes = settings.payrollIncomeTypes ?? [];

        this.hasTimeClock = await this.featureService.has(FeatureFlag.timeClock);

        this.table = new TimesheetsEmployeeIndexTable(
            this.auth,
            this.employee.id,
            hasPayrollIntegration,
            this.hasTimeClock,
            payrollIncomeTypes
        );

        this.table.reload();
    }

    menuAction(event: MenuColumnEvent<TimesheetEntry>): void {
        const { menuItem, data } = event;
        if (!data) {
            return;
        }

        switch (menuItem.event) {
            case 'edit':
                this.onEdit(data);
                break;
            case 'delete':
                this.delete(data);
                break;
        }
    }

    onAction(action: Action<TimesheetEntry>): void {
        switch (action.event) {
            case 'approve':
                this.approve(action.data);
                break;
        }
    }

    onEdit(timesheetEntry: TimesheetEntry): void {
        this.router.navigate(['/time-tracking', 'timesheets', 'employee', timesheetEntry.id, 'edit'], {
            relativeTo: this.route,
        });
    }

    async approve(timesheetEntry: TimesheetEntry): Promise<void> {
        this.isLoading = true;

        try {
            await this.timesheetEntryService.approve(timesheetEntry);
            this.table?.reload();
        } catch (e) {
            this.notifyService.error(
                PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status) ? e : 'time-tracking.approveError'
            );
        }

        this.isLoading = false;
    }

    async delete(timesheetEntry: TimesheetEntry): Promise<void> {
        const shouldDelete = await this.confirmDeleteModal.open();
        if (!shouldDelete) {
            return;
        }

        try {
            await timesheetEntry.delete();
            this.table.reload();
        } catch (e) {
            this.notifyService.error(
                PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status)
                    ? e
                    : 'time-tracking.deleteErrorTimesheetEntry'
            );
        }
    }

    routeTo(): void {
        this.router.navigate(['/time-tracking', 'timesheets', 'employee', this.employee.id, 'create']);
    }

    async onExport(format: 'csv' | 'xlsx', urlExtension: string): Promise<void> {
        try {
            const data = await this.reportDownloadService.downloadFileForEmployee(
                urlExtension,
                this.employee,
                this.table.paginator.currentQuery.searchPayload()
            );
            FileSaver.saveAs(data, `TimeTrackingEmployeeReport.${format}`);
        } catch (err) {
            this.notifyService.error(err);
        }
    }
}
